import { useState, useEffect, useContext } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  DollarSign,
  List,
  PiggyBank,
  FileText,
  Settings,
  LogOut,
  Building2,
  CheckCircle,
  Ban,
  Download,
  Eye,
  Edit,
  X,
  Save,
  Trash2
} from 'lucide-react';
import { LanguageContext } from '../contexts/LanguageContext';
import { getTranslation } from '../i18n/translations';

const AdminDashboard = ({ user, onLogout }) => {
  const location = useLocation();
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const isActive = (path) => {
    return location.pathname === `/admin${path}` || (path === '' && location.pathname === '/admin');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Building2 className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">{t('admin.panel')}</span>
          </div>
          <p className="text-sm text-gray-600 mt-2">{user.name}</p>
        </div>

        <nav className="flex-1 p-4 space-y-2">
          <Link
            to="/admin"
            className={`sidebar-link ${isActive('') ? 'active' : ''}`}
            data-testid="admin-nav-stats"
          >
            <LayoutDashboard className="h-5 w-5 mr-3" />
            {t('admin.statistics')}
          </Link>
          <Link
            to="/admin/users"
            className={`sidebar-link ${isActive('/users') ? 'active' : ''}`}
            data-testid="admin-nav-users"
          >
            <Users className="h-5 w-5 mr-3" />
            {t('admin.users')}
          </Link>
          <Link
            to="/admin/distribute"
            className={`sidebar-link ${isActive('/distribute') ? 'active' : ''}`}
            data-testid="admin-nav-distribute"
          >
            <DollarSign className="h-5 w-5 mr-3" />
            {t('admin.distribute')}
          </Link>
          <Link
            to="/admin/payment-queue"
            className={`sidebar-link ${isActive('/payment-queue') ? 'active' : ''}`}
            data-testid="admin-nav-queue"
          >
            <List className="h-5 w-5 mr-3" />
            {t('admin.paymentQueue')}
          </Link>
          <Link
            to="/admin/reserve-fund"
            className={`sidebar-link ${isActive('/reserve-fund') ? 'active' : ''}`}
            data-testid="admin-nav-reserve"
          >
            <PiggyBank className="h-5 w-5 mr-3" />
            {t('admin.reserveFund')}
          </Link>
          <Link
            to="/admin/reports"
            className={`sidebar-link ${isActive('/reports') ? 'active' : ''}`}
            data-testid="admin-nav-reports"
          >
            <FileText className="h-5 w-5 mr-3" />
            {t('admin.reports')}
          </Link>
          <Link
            to="/admin/backups"
            className={`sidebar-link ${isActive('/backups') ? 'active' : ''}`}
            data-testid="admin-nav-backups"
          >
            <Download className="h-5 w-5 mr-3" />
            Резервные копии
          </Link>
          <Link
            to="/admin/settings"
            className={`sidebar-link ${isActive('/settings') ? 'active' : ''}`}
            data-testid="admin-nav-settings"
          >
            <Settings className="h-5 w-5 mr-3" />
            {t('admin.settings')}
          </Link>
        </nav>

        <div className="p-4 border-t border-gray-200">
          <Button
            variant="outline"
            className="w-full"
            onClick={onLogout}
            data-testid="admin-logout-button"
          >
            <LogOut className="h-4 w-4 mr-2" />
            {t('logout')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <Routes>
          <Route path="/" element={<Statistics />} />
          <Route path="/users" element={<UsersManagement />} />
          <Route path="/distribute" element={<DistributeFunds />} />
          <Route path="/payment-queue" element={<PaymentQueue />} />
          <Route path="/reserve-fund" element={<ReserveFund />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/backups" element={<Backups />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Routes>
      </div>
    </div>
  );
};

const Statistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`);
      setStats(response.data);
    } catch (error) {
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-statistics">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('admin.platformStats')}</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.totalUsers')}</h3>
          <div className="text-3xl font-bold text-blue-600" data-testid="stat-total-users">{stats.total_users}</div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.activeUsers')}</h3>
          <div className="text-3xl font-bold text-green-600" data-testid="stat-active-users">{stats.active_users}</div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.pendingApproval')}</h3>
          <div className="text-3xl font-bold text-yellow-600" data-testid="stat-pending-users">{stats.pending_users}</div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.totalAllAccounts')}</h3>
          <div className="text-3xl font-bold text-emerald-600" data-testid="stat-total-all-accounts">
            ${stats.total_all_accounts?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.totalAccount1')}</h3>
          <div className="text-3xl font-bold text-teal-600" data-testid="stat-total-account1">
            ${stats.total_account1_balance?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.totalAccount2')}</h3>
          <div className="text-3xl font-bold text-cyan-600" data-testid="stat-total-account2">
            ${stats.total_account2_balance?.toFixed(2) || '0.00'}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.totalDistributed')}</h3>
          <div className="text-3xl font-bold text-blue-600" data-testid="stat-total-distributed">
            ${(stats.total_distributed || 0).toFixed(2)}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.lastDistribution')}</h3>
          <div className="text-3xl font-bold text-blue-600" data-testid="stat-last-distribution">
            ${(stats.last_distribution_amount || 0).toFixed(2)}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.reserveFundBalance')}</h3>
          <div className="text-3xl font-bold text-purple-600" data-testid="stat-reserve-fund">
            ${(stats.reserve_fund_balance || 0).toFixed(2)}
          </div>
        </div>
        <div className="data-card">
          <h3 className="text-gray-600 text-sm font-semibold mb-2">{t('admin.averagePayment')}</h3>
          <div className="text-3xl font-bold text-blue-600" data-testid="stat-avg-payment">
            ${(stats.average_payment || 0).toFixed(2)}
          </div>
        </div>
      </div>
    </div>
  );
};

const UsersManagement = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [editingAccounts, setEditingAccounts] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editUserData, setEditUserData] = useState({
    id: '',
    email: '',
    name: '',
    status: 'active',
    account1_balance: 0,
    account1_currency: 'USD',
    account2_balance: 0,
    account2_currency: 'EUR'
  });
  const [createUserData, setCreateUserData] = useState({
    email: '',
    name: '',
    account1_balance: 0,
    account1_currency: 'USD',
    account2_balance: 0,
    account2_currency: 'EUR',
    send_welcome_email: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data.users);
    } catch (error) {
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setSelectedUser(users.find(u => u.id === userId));
  };

  const handleViewDetails = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/user/${userId}/details`);
      setUserDetails(response.data);
      setShowDetails(true);
    } catch (error) {
      toast.error(t('messages.failedToLoad'));
    }
  };

  const handleUpdateAccounts = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.put(`${API}/admin/user/${userDetails.user.id}/accounts`, {
        account1_balance: parseFloat(formData.get('account1_balance')),
        account1_currency: formData.get('account1_currency'),
        account2_balance: parseFloat(formData.get('account2_balance')),
        account2_currency: formData.get('account2_currency')
      });
      
      toast.success(t('admin.accountsUpdated'));
      setEditingAccounts(false);
      fetchUsers();
      handleViewDetails(userDetails.user.id); // Refresh details
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleApproveSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await axios.put(`${API}/admin/user/${selectedUser.id}/approve`, {
        account1_balance: parseFloat(formData.get('account1_balance')),
        account1_currency: formData.get('account1_currency'),
        account2_balance: parseFloat(formData.get('account2_balance')),
        account2_currency: formData.get('account2_currency')
      });
      toast.success(t('admin.userApproved'));
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleBlock = async (userId) => {
    try {
      const response = await axios.put(`${API}/admin/user/${userId}/block`);
      toast.success(t('admin.userBlocked'));
      fetchUsers();
    } catch (error) {
      toast.error(t('messages.error'));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/create-user`, createUserData);
      toast.success('Пользователь успешно создан!');
      setShowCreateUser(false);
      setCreateUserData({
        email: '',
        name: '',
        account1_balance: 0,
        account1_currency: 'USD',
        account2_balance: 0,
        account2_currency: 'EUR',
        send_welcome_email: false
      });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания пользователя');
    }
  };

  const handleEditUser = (user) => {
    // Get user accounts
    const account1 = user.accounts?.find(acc => acc.account_number === 1) || { balance: 0, currency: 'USD' };
    const account2 = user.accounts?.find(acc => acc.account_number === 2) || { balance: 0, currency: 'EUR' };
    
    setEditUserData({
      id: user.id,
      email: user.email,
      name: user.name,
      status: user.status,
      account1_balance: account1.balance,
      account1_currency: account1.currency,
      account2_balance: account2.balance,
      account2_currency: account2.currency
    });
    setShowEditUser(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/user/${editUserData.id}`, {
        email: editUserData.email,
        name: editUserData.name,
        status: editUserData.status,
        account1_balance: editUserData.account1_balance,
        account1_currency: editUserData.account1_currency,
        account2_balance: editUserData.account2_balance,
        account2_currency: editUserData.account2_currency
      });
      toast.success('Данные пользователя обновлены!');
      setShowEditUser(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка обновления пользователя');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Вы уверены, что хотите удалить пользователя "${userName}"?\n\nЭто действие удалит:\n- Пользователя\n- Все его счета\n- Все реквизиты\n- Историю выплат\n- Все уведомления\n\nЭто действие необратимо!`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/user/${userId}`);
      toast.success('Пользователь успешно удалён!');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка удаления пользователя');
    }
  };

  return (
    <div className="p-8" data-testid="admin-users-management">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('admin.users')}</h1>
        <Button onClick={() => setShowCreateUser(true)} className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Создать пользователя
        </Button>
      </div>

      {/* Create User Modal */}
      {showCreateUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Создать нового пользователя</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={createUserData.email}
                  onChange={(e) => setCreateUserData({...createUserData, email: e.target.value})}
                  required
                  placeholder="user@example.com"
                />
              </div>
              <div>
                <Label>Имя *</Label>
                <Input
                  type="text"
                  value={createUserData.name}
                  onChange={(e) => setCreateUserData({...createUserData, name: e.target.value})}
                  required
                  placeholder="Иван Иванов"
                />
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Счёт 1 (USD)</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Баланс счёта 1</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={createUserData.account1_balance}
                      onChange={(e) => setCreateUserData({...createUserData, account1_balance: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Валюта счёта 1</Label>
                    <Select
                      value={createUserData.account1_currency}
                      onValueChange={(value) => setCreateUserData({...createUserData, account1_currency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Счёт 2 (EUR)</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Баланс счёта 2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={createUserData.account2_balance}
                      onChange={(e) => setCreateUserData({...createUserData, account2_balance: parseFloat(e.target.value) || 0})}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <Label>Валюта счёта 2</Label>
                    <Select
                      value={createUserData.account2_currency}
                      onValueChange={(value) => setCreateUserData({...createUserData, account2_currency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 border-t pt-4">
                <input
                  type="checkbox"
                  id="send_welcome_email"
                  checked={createUserData.send_welcome_email}
                  onChange={(e) => setCreateUserData({...createUserData, send_welcome_email: e.target.checked})}
                  className="rounded"
                />
                <Label htmlFor="send_welcome_email" className="cursor-pointer">
                  Отправить приветственное письмо с PDF соглашением
                </Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1">Создать пользователя</Button>
                <Button type="button" variant="outline" onClick={() => setShowCreateUser(false)} className="flex-1">Отмена</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Редактировать пользователя</h2>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editUserData.email}
                  onChange={(e) => setEditUserData({...editUserData, email: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Имя *</Label>
                <Input
                  type="text"
                  value={editUserData.name}
                  onChange={(e) => setEditUserData({...editUserData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Статус</Label>
                <Select
                  value={editUserData.status}
                  onValueChange={(value) => setEditUserData({...editUserData, status: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="blocked">Blocked</SelectItem>
                    <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Счёт 1</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Баланс счёта 1</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editUserData.account1_balance}
                      onChange={(e) => setEditUserData({...editUserData, account1_balance: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Валюта счёта 1</Label>
                    <Select
                      value={editUserData.account1_currency}
                      onValueChange={(value) => setEditUserData({...editUserData, account1_currency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="border-t pt-4">
                <h3 className="font-semibold mb-3">Счёт 2</h3>
                <div className="space-y-3">
                  <div>
                    <Label>Баланс счёта 2</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editUserData.account2_balance}
                      onChange={(e) => setEditUserData({...editUserData, account2_balance: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <Label>Валюта счёта 2</Label>
                    <Select
                      value={editUserData.account2_currency}
                      onValueChange={(value) => setEditUserData({...editUserData, account2_currency: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">Сохранить изменения</Button>
                <Button type="button" variant="outline" onClick={() => setShowEditUser(false)} className="flex-1">Отмена</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">{t('admin.approveUser')}:  {selectedUser.name}</h2>
            <form onSubmit={handleApproveSubmit} className="space-y-4">
              <div>
                <Label>{t('admin.account1Balance')}</Label>
                <Input name="account1_balance" type="number" step="0.01" required data-testid="account1-balance-input" />
              </div>
              <div>
                <Label>{t('admin.account1Currency')}</Label>
                <Select name="account1_currency" required>
                  <SelectTrigger data-testid="account1-currency-select">
                    <SelectValue placeholder={t('admin.selectCurrency')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('admin.account2Balance')}</Label>
                <Input name="account2_balance" type="number" step="0.01" required data-testid="account2-balance-input" />
              </div>
              <div>
                <Label>{t('admin.account2Currency')}</Label>
                <Select name="account2_currency" required>
                  <SelectTrigger data-testid="account2-currency-select">
                    <SelectValue placeholder={t('admin.selectCurrency')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="submit-approve-button">{t('admin.approve')}</Button>
                <Button variant="outline" onClick={() => setSelectedUser(null)}>{t('cancel')}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <div className="data-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="text-left p-3">{t('name')}</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">{t('status')}</th>
                <th className="text-left p-3">{t('admin.account1')}</th>
                <th className="text-left p-3">{t('admin.account2')}</th>
                <th className="text-left p-3">{t('admin.totalPayments')}</th>
                <th className="text-left p-3">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const account1 = user.accounts?.find(a => a.account_type === 'account1');
                const account2 = user.accounts?.find(a => a.account_type === 'account2');
                
                return (
                  <tr key={user.id} className="border-t border-gray-200" data-testid="user-row">
                    <td className="p-3 font-medium">{user.name}</td>
                    <td className="p-3">{user.email}</td>
                    <td className="p-3">
                      <span className={`status-badge status-${user.status}`}>
                        {user.status}
                      </span>
                    </td>
                    <td className="p-3">
                      {account1 ? (
                        <span className="text-teal-600 font-semibold">
                          ${account1.balance?.toFixed(2)} {account1.currency}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      {account2 ? (
                        <span className="text-cyan-600 font-semibold">
                          ${account2.balance?.toFixed(2)} {account2.currency}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className="text-blue-600 font-semibold">
                        ${user.total_payments?.toFixed(2) || '0.00'}
                      </span>
                    </td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(user.id)}
                          data-testid="view-user-details-button"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {t('admin.viewDetails')}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditUser(user)}
                          className="bg-blue-50 hover:bg-blue-100 text-blue-600 border-blue-200"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Редактировать
                        </Button>
                        {user.status === 'pending_approval' && (
                          <Button
                            size="sm"
                            onClick={() => handleApprove(user.id)}
                            data-testid="approve-user-button"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            {t('admin.approve')}
                          </Button>
                        )}
                        {user.status !== 'pending_approval' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleBlock(user.id)}
                            data-testid="block-user-button"
                          >
                            <Ban className="h-4 w-4 mr-1" />
                            {user.status === 'blocked' ? t('admin.unblock') : t('admin.block')}
                          </Button>
                        )}
                        {!user.is_admin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteUser(user.id, user.name)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 border-red-200"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Удалить
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* User Details Modal */}
      {showDetails && userDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">{userDetails.user.name}</h2>
                <p className="text-blue-100 text-sm">{userDetails.user.email}</p>
              </div>
              <button
                onClick={() => setShowDetails(false)}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="data-card">
                  <h3 className="text-sm text-gray-600 mb-1">{t('status')}</h3>
                  <span className={`status-badge status-${userDetails.user.status}`}>
                    {userDetails.user.status}
                  </span>
                </div>
                <div className="data-card">
                  <h3 className="text-sm text-gray-600 mb-1">{t('admin.registeredDate')}</h3>
                  <p className="text-lg font-semibold">
                    {new Date(userDetails.user.created_at).toLocaleDateString('ru-RU')}
                  </p>
                </div>
              </div>

              {/* Accounts Section */}
              <div className="data-card">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-bold text-gray-900">{t('admin.accounts')}</h3>
                  {!editingAccounts && (
                    <Button size="sm" onClick={() => setEditingAccounts(true)}>
                      <Edit className="h-4 w-4 mr-1" />
                      {t('admin.editAccounts')}
                    </Button>
                  )}
                </div>

                {!editingAccounts ? (
                  <div className="grid grid-cols-2 gap-4">
                    {userDetails.accounts.map((account) => (
                      <div key={account.id} className="border border-gray-200 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-600 mb-2">
                          {account.account_type === 'account1' ? t('admin.account1') : t('admin.account2')}
                        </h4>
                        <p className="text-2xl font-bold text-blue-600">
                          ${(account.balance || 0).toFixed(2)} {account.currency}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {t('admin.accountNumber')}: {account.account_number}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <form onSubmit={handleUpdateAccounts} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>{t('admin.account1Balance')}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          name="account1_balance"
                          defaultValue={userDetails.accounts[0]?.balance || 0}
                          required
                        />
                      </div>
                      <div>
                        <Label>{t('admin.account1Currency')}</Label>
                        <Select name="account1_currency" defaultValue={userDetails.accounts[0]?.currency || 'USD'}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{t('admin.account2Balance')}</Label>
                        <Input
                          type="number"
                          step="0.01"
                          name="account2_balance"
                          defaultValue={userDetails.accounts[1]?.balance || 0}
                          required
                        />
                      </div>
                      <div>
                        <Label>{t('admin.account2Currency')}</Label>
                        <Select name="account2_currency" defaultValue={userDetails.accounts[1]?.currency || 'USD'}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit">
                        <Save className="h-4 w-4 mr-1" />
                        {t('admin.saveChanges')}
                      </Button>
                      <Button type="button" variant="outline" onClick={() => setEditingAccounts(false)}>
                        {t('cancel')}
                      </Button>
                    </div>
                  </form>
                )}
              </div>

              {/* Credentials Section */}
              <div className="data-card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('admin.paymentCredentials')}</h3>
                {userDetails.credentials.length > 0 ? (
                  <div className="space-y-3">
                    {userDetails.credentials.map((cred) => (
                      <div key={cred.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-semibold text-gray-700">{cred.type.replace('_', ' ').toUpperCase()}</span>
                          {cred.is_primary && (
                            <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                              {t('admin.primary')}
                            </span>
                          )}
                        </div>
                        {cred.label && (
                          <p className="text-gray-600 text-sm mb-2">{cred.label}</p>
                        )}
                        
                        {cred.type === 'bank' ? (
                          // Display bank details structured
                          (() => {
                            try {
                              const bankDetails = JSON.parse(cred.details);
                              return (
                                <div className="bg-gray-50 p-3 rounded space-y-2">
                                  <div>
                                    <span className="text-xs text-gray-500 font-semibold">IBAN:</span>
                                    <p className="font-mono text-sm text-gray-900 break-all">{bankDetails.iban}</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500 font-semibold">{t('admin.beneficiaryName')}:</span>
                                    <p className="text-sm text-gray-900">{bankDetails.beneficiary_name}</p>
                                  </div>
                                  <div>
                                    <span className="text-xs text-gray-500 font-semibold">{t('admin.bankName')}:</span>
                                    <p className="text-sm text-gray-900">{bankDetails.bank_name}</p>
                                  </div>
                                </div>
                              );
                            } catch (e) {
                              return (
                                <p className="text-sm text-gray-500 mt-1 font-mono break-all">{cred.details}</p>
                              );
                            }
                          })()
                        ) : (
                          <div className="bg-gray-50 p-3 rounded">
                            <span className="text-xs text-gray-500 font-semibold">{t('admin.walletAddress')}:</span>
                            <p className="text-sm text-gray-900 font-mono break-all mt-1">{cred.details}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">{t('admin.noCredentials')}</p>
                )}
              </div>

              {/* Statistics Section */}
              <div className="data-card">
                <h3 className="text-xl font-bold text-gray-900 mb-4">{t('admin.userStatistics')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('admin.totalPayments')}</p>
                    <p className="text-2xl font-bold text-blue-600">{userDetails.statistics.total_payments}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('admin.totalReceived')}</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${userDetails.statistics.total_received.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('admin.pendingAmount')}</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      ${userDetails.statistics.total_pending.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600">{t('admin.notifications')}</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {userDetails.statistics.unread_notifications}/{userDetails.statistics.notifications_count}
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent Payments */}
              {userDetails.recent_payments.length > 0 && (
                <div className="data-card">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t('admin.recentPayments')}</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left p-2">{t('date')}</th>
                        <th className="text-left p-2">{t('amount')}</th>
                        <th className="text-left p-2">{t('status')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {userDetails.recent_payments.map((payment) => (
                        <tr key={payment.id} className="border-b border-gray-100">
                          <td className="p-2">
                            {new Date(payment.created_at).toLocaleDateString('ru-RU')}
                          </td>
                          <td className="p-2 font-semibold">${payment.amount.toFixed(2)}</td>
                          <td className="p-2">
                            <span className={`status-badge status-${payment.status}`}>
                              {payment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const DistributeFunds = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [eurUsdRate, setEurUsdRate] = useState('1.08');  // Default EUR/USD rate
  const [reservePercent, setReservePercent] = useState(10);
  const [accountSource, setAccountSource] = useState(1);  // Default to account 1
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [customDistribution, setCustomDistribution] = useState(false);
  const [individualAmount, setIndividualAmount] = useState('');
  const [usersList, setUsersList] = useState([]);
  const [individualPayments, setIndividualPayments] = useState({});

  // Convert amount based on currencies
  const convertAmount = (baseAmount, fromCurrency, toCurrency, rate) => {
    if (fromCurrency === toCurrency) return baseAmount;
    if (fromCurrency === 'USD' && toCurrency === 'EUR') {
      return Math.round((baseAmount / rate) * 100) / 100;
    }
    if (fromCurrency === 'EUR' && toCurrency === 'USD') {
      return Math.round((baseAmount * rate) * 100) / 100;
    }
    return baseAmount;
  };

  const handlePreview = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Введите корректную сумму');
      return;
    }

    if (!eurUsdRate || parseFloat(eurUsdRate) <= 0) {
      toast.error('Введите корректный курс EUR/USD');
      return;
    }

    try {
      // Get active users with details
      const usersResponse = await axios.get(`${API}/admin/users`);
      const activeUsers = usersResponse.data.users.filter(u => u.status === 'active');
      
      const statsResponse = await axios.get(`${API}/admin/stats`);
      const currentReserve = statsResponse.data.reserve_fund_balance || 0;
      
      if (activeUsers.length === 0) {
        toast.error(t('admin.noActiveUsers'));
        return;
      }

      const totalAmount = parseFloat(amount);
      const rate = parseFloat(eurUsdRate);
      const reservePercentValue = parseFloat(reservePercent) / 100;
      const amountForDistribution = totalAmount * (1 - reservePercentValue);
      
      let calcIndividualAmount;
      let totalDistributed;
      let reserveAmount;
      let usersWithAmounts = [];

      if (customDistribution && individualAmount) {
        // Custom distribution - same amount for all (in base currency)
        calcIndividualAmount = parseFloat(individualAmount);
        totalDistributed = calcIndividualAmount * activeUsers.length;
        reserveAmount = totalAmount - totalDistributed;
        
        usersWithAmounts = activeUsers.map(user => {
          // Get user's account currency
          const account1 = user.accounts?.find(a => a.account_number === 1);
          const userCurrency = account1?.currency || 'USD';
          const convertedAmount = convertAmount(calcIndividualAmount, currency, userCurrency, rate);
          
          return {
            ...user,
            payment_amount: calcIndividualAmount,  // Base amount
            converted_amount: convertedAmount,     // In user's currency
            user_currency: userCurrency
          };
        });
      } else {
        // Automatic distribution with reserve percent
        calcIndividualAmount = amountForDistribution / activeUsers.length;
        totalDistributed = calcIndividualAmount * activeUsers.length;
        reserveAmount = totalAmount - totalDistributed;
        
        usersWithAmounts = activeUsers.map(user => {
          // Get user's account currency
          const account1 = user.accounts?.find(a => a.account_number === 1);
          const userCurrency = account1?.currency || 'USD';
          const convertedAmount = convertAmount(calcIndividualAmount, currency, userCurrency, rate);
          
          return {
            ...user,
            payment_amount: calcIndividualAmount,  // Base amount
            converted_amount: convertedAmount,     // In user's currency
            user_currency: userCurrency
          };
        });
      }

      setUsersList(usersWithAmounts);
      setPreview({
        totalAmount,
        activeUsers: activeUsers.length,
        individualAmount: calcIndividualAmount,
        totalDistributed,
        reserveAmount,
        reservePercent: ((reserveAmount / totalAmount) * 100).toFixed(2),
        currentReserve,
        newReserveTotal: currentReserve + reserveAmount,
        eurUsdRate: rate
      });
    } catch (error) {
      toast.error('Ошибка расчёта');
    }
  };

  const handleUserAmountChange = (userId, newAmount) => {
    const rate = parseFloat(eurUsdRate) || 1;
    const updatedUsers = usersList.map(user => {
      if (user.id === userId) {
        const baseAmount = parseFloat(newAmount) || 0;
        const convertedAmount = convertAmount(baseAmount, currency, user.user_currency, rate);
        return { 
          ...user, 
          payment_amount: baseAmount,
          converted_amount: convertedAmount
        };
      }
      return user;
    });
    setUsersList(updatedUsers);
    
    // Recalculate totals
    const totalDistributed = updatedUsers.reduce((sum, user) => sum + user.payment_amount, 0);
    const reserveAmount = preview.totalAmount - totalDistributed;
    
    setPreview({
      ...preview,
      totalDistributed,
      reserveAmount,
      reservePercent: ((reserveAmount / preview.totalAmount) * 100).toFixed(2),
      newReserveTotal: preview.currentReserve + reserveAmount
    });
  };

  const handleDistribute = async () => {
    if (!preview) {
      toast.error('Сначала выполните расчёт');
      return;
    }

    setLoading(true);
    try {
      // Send individual payments for each user (in base currency)
      const userPayments = usersList.map(user => ({
        user_id: user.id,
        amount: user.payment_amount
      }));

      await axios.post(`${API}/admin/distribute`, {
        amount: parseFloat(amount),
        currency,
        eur_usd_rate: parseFloat(eurUsdRate),
        account_source: accountSource,
        user_payments: userPayments
      });
      toast.success(t('admin.distributionSuccess'));
      setAmount('');
      setReservePercent(10);
      setAccountSource(1);
      setPreview(null);
      setCustomDistribution(false);
      setIndividualAmount('');
      setUsersList([]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка распределения');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-8" data-testid="admin-distribute-funds">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('admin.distributeFunds')}</h1>

      <div className="max-w-3xl">
        <div className="data-card mb-6">
          <h3 className="font-semibold text-lg mb-4">{t('admin.calculator')}</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>{t('admin.totalAmount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => {
                    setAmount(e.target.value);
                    setPreview(null);
                  }}
                  placeholder={t('admin.enterAmount')}
                  data-testid="distribute-amount-input"
                />
              </div>
              <div>
                <Label>{t('currency')}</Label>
                <Select value={currency} onValueChange={(val) => {
                  setCurrency(val);
                  setPreview(null);
                }}>
                  <SelectTrigger data-testid="distribute-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD</SelectItem>
                    <SelectItem value="EUR">EUR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Курс EUR/USD</Label>
                <Input
                  type="number"
                  step="0.0001"
                  min="0.01"
                  value={eurUsdRate}
                  onChange={(e) => {
                    setEurUsdRate(e.target.value);
                    setPreview(null);
                  }}
                  placeholder="1.08"
                  data-testid="eur-usd-rate-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  1 EUR = {eurUsdRate || '1.08'} USD
                </p>
              </div>
            </div>

            <div>
              <Label>{t('admin.sourceAccount')}</Label>
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm font-semibold text-blue-900">
                  {t('admin.autoAccountSelection')}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  {t('admin.autoAccountHint')}
                </p>
              </div>
            </div>

            <div>
              <Label>{t('admin.reservePercent')} (%)</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                max="100"
                value={reservePercent}
                onChange={(e) => {
                  setReservePercent(e.target.value);
                  setPreview(null);
                }}
                placeholder="10"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('admin.reservePercentHint')}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="customDistribution"
                checked={customDistribution}
                onChange={(e) => {
                  setCustomDistribution(e.target.checked);
                  setPreview(null);
                }}
                className="rounded"
              />
              <Label htmlFor="customDistribution" className="cursor-pointer">
                {t('admin.customDistribution')}
              </Label>
            </div>

            {customDistribution && (
              <div>
                <Label>{t('admin.individualPaymentAmount')}</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={individualAmount}
                  onChange={(e) => {
                    setIndividualAmount(e.target.value);
                    setPreview(null);
                  }}
                  placeholder={t('admin.enterIndividualAmount')}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {t('admin.individualAmountHint')}
                </p>
              </div>
            )}

            <Button onClick={handlePreview} className="w-full" data-testid="preview-distribution-button">
              {t('admin.calculate')}
            </Button>
          </div>
        </div>

        {preview && (
          <div className="data-card mb-6">
            <h3 className="font-semibold text-lg mb-4">{t('admin.preview')}</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-4 pb-3 border-b">
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">{t('admin.totalAmount')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    ${preview.totalAmount.toFixed(2)}
                  </p>
                  <p className="text-xs text-gray-500">{currency}</p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-1">{t('admin.activeUsers')}</p>
                  <p className="text-2xl font-bold text-gray-900">{preview.activeUsers}</p>
                  <p className="text-xs text-gray-500">{t('admin.recipients')}</p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-blue-900">{t('admin.individualPayment')}</span>
                  <span className="text-2xl font-bold text-blue-600">
                    ${preview.individualAmount.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-blue-700">{t('admin.totalToUsers')}</span>
                  <span className="font-semibold text-blue-700">
                    ${preview.totalDistributed.toFixed(2)} {currency}
                  </span>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-semibold text-purple-900">
                    {t('admin.reserveAllocation')} ({preview.reservePercent}%)
                  </span>
                  <span className="text-2xl font-bold text-purple-600">
                    ${preview.reserveAmount.toFixed(2)}
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-purple-700">{t('admin.currentReserve')}</span>
                    <span className="font-semibold text-purple-700">
                      ${preview.currentReserve.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between border-t border-purple-200 pt-1">
                    <span className="text-purple-900 font-semibold">{t('admin.newReserveTotal')}</span>
                    <span className="font-bold text-purple-900">
                      ${preview.newReserveTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {customDistribution && (
                <div className="bg-yellow-50 border border-yellow-300 rounded p-3 text-sm">
                  <p className="text-yellow-800">
                    ⚠️ {t('admin.customDistributionWarning')}
                  </p>
                </div>
              )}
            </div>

            {/* Users List with Individual Amounts */}
            {usersList.length > 0 && (
              <div className="mt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-semibold text-lg">{t('admin.individualPayments')}</h3>
                  <div className="text-sm text-gray-600">
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded mr-2">
                      Курс: 1 EUR = {preview?.eurUsdRate || eurUsdRate} USD
                    </span>
                    {t('admin.clickToEdit')}
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-3 font-semibold">{t('admin.user')}</th>
                        <th className="text-left p-3 font-semibold">Email</th>
                        <th className="text-center p-3 font-semibold">Валюта счёта</th>
                        <th className="text-right p-3 font-semibold">Сумма ({currency})</th>
                        <th className="text-right p-3 font-semibold">К выплате</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usersList.map((user, idx) => (
                        <tr key={user.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-3 font-medium">{user.name}</td>
                          <td className="p-3 text-gray-600">{user.email}</td>
                          <td className="p-3 text-center">
                            <span className={`px-2 py-1 rounded text-xs font-semibold ${
                              user.user_currency === 'EUR' 
                                ? 'bg-blue-100 text-blue-700' 
                                : 'bg-green-100 text-green-700'
                            }`}>
                              {user.user_currency}
                            </span>
                          </td>
                          <td className="p-3 text-right">
                            <Input
                              type="number"
                              step="0.01"
                              value={user.payment_amount}
                              onChange={(e) => handleUserAmountChange(user.id, e.target.value)}
                              className="w-28 text-right ml-auto"
                            />
                          </td>
                          <td className="p-3 text-right font-semibold">
                            <span className={user.user_currency === 'EUR' ? 'text-blue-600' : 'text-green-600'}>
                              {user.converted_amount?.toFixed(2)} {user.user_currency}
                            </span>
                            {user.user_currency !== currency && (
                              <span className="text-xs text-gray-400 block">
                                ({user.payment_amount?.toFixed(2)} {currency})
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-100 border-t-2 border-gray-300">
                      <tr>
                        <td colSpan="3" className="p-3 font-bold text-gray-900">
                          {t('admin.totalDistribution')}
                        </td>
                        <td className="p-3 text-right font-bold text-blue-600">
                          {preview.totalDistributed.toFixed(2)} {currency}
                        </td>
                        <td className="p-3 text-right text-sm text-gray-600">
                          (с конвертацией)
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  💡 Суммы автоматически конвертируются в валюту счёта каждого пользователя по указанному курсу
                </p>
              </div>
            )}

            <Button
              onClick={handleDistribute}
              disabled={loading}
              className="w-full mt-4"
              data-testid="confirm-distribution-button"
            >
              {loading ? t('admin.processing') : t('admin.confirmDistribution')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const PaymentQueue = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'completed'
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-queue`);
      setPayments(response.data.payments);
    } catch (error) {
      toast.error(t('messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (paymentId) => {
    try {
      await axios.put(`${API}/admin/payment-queue/${paymentId}/confirm`);
      toast.success(t('admin.paymentConfirmed'));
      fetchPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('messages.error'));
    }
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
  };

  const filteredPayments = payments.filter(p => {
    if (filter === 'all') return true;
    return p.status === filter;
  });

  return (
    <div className="p-8" data-testid="admin-payment-queue">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('admin.queue')}</h1>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48" data-testid="payment-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('admin.allPayments')}</SelectItem>
            <SelectItem value="pending">{t('user.pending')}</SelectItem>
            <SelectItem value="completed">{t('user.completed')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : filteredPayments.length === 0 ? (
        <div className="data-card text-center py-12">
          <List className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('admin.noPaymentsInQueue')}</p>
        </div>
      ) : (
        <div className="data-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="text-left p-3">{t('admin.user')}</th>
                <th className="text-left p-3">{t('amount')}</th>
                <th className="text-left p-3">{t('admin.credentials')}</th>
                <th className="text-left p-3">{t('status')}</th>
                <th className="text-left p-3">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map((payment) => (
                <tr key={payment.id} className="border-t border-gray-200" data-testid="payment-queue-row">
                  <td className="p-3">
                    <div>
                      <div className="font-semibold">{payment.user_name}</div>
                      <div className="text-sm text-gray-600">{payment.user_email}</div>
                    </div>
                  </td>
                  <td className="p-3 font-semibold">
                    ${payment.amount} {payment.currency}
                  </td>
                  <td className="p-3 max-w-xs">
                    {payment.credential ? (
                      <div className="text-sm">
                        <div className="font-medium mb-1">{payment.credential.type.replace('_', ' ').toUpperCase()}</div>
                        {payment.credential.type === 'bank' ? (
                          // Display bank details structured
                          (() => {
                            try {
                              const bankDetails = JSON.parse(payment.credential.details);
                              return (
                                <div className="text-xs space-y-1 bg-gray-50 p-2 rounded">
                                  <div><span className="font-semibold">IBAN:</span> <span className="font-mono break-all">{bankDetails.iban}</span></div>
                                  <div><span className="font-semibold">{t('admin.beneficiaryName')}:</span> {bankDetails.beneficiary_name}</div>
                                  <div><span className="font-semibold">{t('admin.bankName')}:</span> {bankDetails.bank_name}</div>
                                </div>
                              );
                            } catch (e) {
                              return (
                                <div className="text-gray-600 font-mono text-xs break-all">
                                  {payment.credential.details}
                                </div>
                              );
                            }
                          })()
                        ) : (
                          <div className="text-gray-600 font-mono text-xs break-all">
                            {payment.credential.details}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="text-red-600 text-sm">{t('admin.noCredentials')}</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`status-badge status-${payment.status}`}>
                      {payment.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleViewDetails(payment)}
                        data-testid="view-details-button"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        {t('admin.viewDetails')}
                      </Button>
                      {payment.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleConfirm(payment.id)}
                          data-testid="confirm-payment-button"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          {t('admin.confirmSent')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payment Details Modal */}
      {showDetailsModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">{t('admin.paymentDetails')}</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* User Information */}
              <div className="data-card mb-4">
                <h3 className="font-semibold text-lg mb-3">{t('admin.userInformation')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('name')}:</span>
                    <span className="font-semibold">{selectedPayment.user_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold">{selectedPayment.user_email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('admin.userId')}:</span>
                    <span className="font-mono text-sm">{selectedPayment.user_id}</span>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="data-card mb-4">
                <h3 className="font-semibold text-lg mb-3">{t('admin.paymentInformation')}</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('amount')}:</span>
                    <span className="font-bold text-xl text-blue-600">
                      ${selectedPayment.amount} {selectedPayment.currency}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('admin.sourceAccount')}:</span>
                    <span className="font-semibold">
                      {t('admin.account' + (selectedPayment.account_source || 1))}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('status')}:</span>
                    <span className={`status-badge status-${selectedPayment.status}`}>
                      {selectedPayment.status}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('admin.createdAt')}:</span>
                    <span>{new Date(selectedPayment.created_at).toLocaleString('ru-RU')}</span>
                  </div>
                  {selectedPayment.confirmed_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('admin.confirmedAt')}:</span>
                      <span>{new Date(selectedPayment.confirmed_at).toLocaleString('ru-RU')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment Credentials */}
              <div className="data-card mb-4">
                <h3 className="font-semibold text-lg mb-3">{t('admin.paymentCredentials')}</h3>
                {selectedPayment.credential ? (
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{t('type')}:</span>
                      <span className="font-semibold">
                        {selectedPayment.credential.type.replace('_', ' ').toUpperCase()}
                      </span>
                    </div>
                    {selectedPayment.credential.label && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('label')}:</span>
                        <span className="font-semibold">{selectedPayment.credential.label}</span>
                      </div>
                    )}
                    
                    {selectedPayment.credential.type === 'bank' ? (
                      // Display bank details structured
                      (() => {
                        try {
                          const bankDetails = JSON.parse(selectedPayment.credential.details);
                          return (
                            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-600 font-semibold">IBAN:</span>
                                <span className="font-mono text-sm">{bankDetails.iban}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 font-semibold">{t('admin.beneficiaryName')}:</span>
                                <span>{bankDetails.beneficiary_name}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-gray-600 font-semibold">{t('admin.bankName')}:</span>
                                <span>{bankDetails.bank_name}</span>
                              </div>
                            </div>
                          );
                        } catch (e) {
                          return (
                            <div className="bg-gray-50 p-4 rounded-lg">
                              <span className="text-gray-600">{t('details')}:</span>
                              <p className="font-mono text-sm break-all mt-2">
                                {selectedPayment.credential.details}
                              </p>
                            </div>
                          );
                        }
                      })()
                    ) : (
                      // Display wallet address
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-gray-600 block mb-2">{t('admin.walletAddress')}:</span>
                        <p className="font-mono text-sm break-all">
                          {selectedPayment.credential.details}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <Ban className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600">{t('admin.noCredentials')}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowDetailsModal(false)}
                >
                  {t('close')}
                </Button>
                {selectedPayment.status === 'pending' && (
                  <Button
                    className="flex-1"
                    onClick={() => {
                      handleConfirm(selectedPayment.id);
                      setShowDetailsModal(false);
                    }}
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    {t('admin.confirmSent')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ReserveFund = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [reserve, setReserve] = useState(null);
  const [loading, setLoading] = useState(true);
  const [distributeAmount, setDistributeAmount] = useState('');
  const [currency, setCurrency] = useState('USD');

  useEffect(() => {
    fetchReserve();
  }, []);

  const fetchReserve = async () => {
    try {
      const response = await axios.get(`${API}/admin/reserve-fund`);
      setReserve(response.data.reserve);
    } catch (error) {
      toast.error('Failed to load reserve fund');
    } finally {
      setLoading(false);
    }
  };

  const handleDistribute = async () => {
    if (!distributeAmount || parseFloat(distributeAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (parseFloat(distributeAmount) > reserve.current_balance) {
      toast.error('Insufficient reserve balance');
      return;
    }

    try {
      await axios.post(`${API}/admin/distribute-reserve`, {
        amount: parseFloat(distributeAmount),
        currency
      });
      toast.success('Reserve funds distributed successfully!');
      setDistributeAmount('');
      fetchReserve();
    } catch (error) {
      toast.error('Failed to distribute reserve');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-reserve-fund">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('admin.reserveFund')}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="data-card">
          <h3 className="font-semibold text-lg mb-4">{t('admin.currentBalance')}</h3>
          <div className="text-4xl font-bold text-purple-600 mb-6" data-testid="reserve-balance">
            ${(reserve.current_balance || 0).toFixed(2)}
          </div>

          <div className="space-y-3">
            <Label>Distribute from Reserve</Label>
            <Input
              type="number"
              step="0.01"
              value={distributeAmount}
              onChange={(e) => setDistributeAmount(e.target.value)}
              placeholder="Amount to distribute"
              data-testid="reserve-distribute-input"
            />
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger data-testid="reserve-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="USD">USD</SelectItem>
                <SelectItem value="EUR">EUR</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleDistribute} className="w-full" data-testid="distribute-reserve-button">
              Distribute Reserve
            </Button>
          </div>
        </div>

        <div className="data-card">
          <h3 className="font-semibold text-lg mb-4">Transaction History</h3>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {reserve.history.length === 0 ? (
              <p className="text-gray-600 text-sm">No transactions yet</p>
            ) : (
              reserve.history.slice().reverse().map((entry, idx) => (
                <div key={idx} className="border-b border-gray-200 pb-2" data-testid="reserve-history-item">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-sm font-medium">{entry.type.replace('_', ' ')}</div>
                      <div className="text-xs text-gray-500">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div className={`font-semibold ${entry.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {entry.amount > 0 ? '+' : ''}${entry.amount.toFixed(2)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const Backups = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      const response = await axios.get(`${API}/admin/backups`);
      setBackups(response.data.backups);
    } catch (error) {
      toast.error('Ошибка загрузки списка бэкапов');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await axios.post(`${API}/admin/create-backup`);
      toast.success(`Бэкап создан: ${response.data.filename}`);
      fetchBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка создания бэкапа');
    } finally {
      setCreating(false);
    }
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBackupTypeBadge = (type) => {
    const types = {
      'auto': { label: 'Автоматический', color: 'bg-blue-100 text-blue-800' },
      'manual': { label: 'Ручной', color: 'bg-green-100 text-green-800' },
      'pre-deployment': { label: 'Pre-deployment', color: 'bg-purple-100 text-purple-800' }
    };
    const badge = types[type] || { label: type, color: 'bg-gray-100 text-gray-800' };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  return (
    <div className="p-8" data-testid="admin-backups">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Резервные копии базы данных</h1>
          <p className="text-gray-600 mt-2">Управление бэкапами и восстановление данных</p>
        </div>
        <Button 
          onClick={handleCreateBackup} 
          disabled={creating}
          className="flex items-center gap-2"
        >
          <Download className="h-5 w-5" />
          {creating ? 'Создание...' : 'Создать бэкап'}
        </Button>
      </div>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Всего бэкапов</p>
              <p className="text-3xl font-bold mt-1">{backups.length}</p>
            </div>
            <Download className="h-12 w-12 text-blue-200" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Автоматические</p>
              <p className="text-3xl font-bold mt-1">
                {backups.filter(b => b.type === 'auto').length}
              </p>
            </div>
            <FileText className="h-12 w-12 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Pre-deployment</p>
              <p className="text-3xl font-bold mt-1">
                {backups.filter(b => b.type === 'pre-deployment').length}
              </p>
            </div>
            <Settings className="h-12 w-12 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <p className="text-sm text-blue-700">
              <strong>Автоматические бэкапы:</strong> Создаются каждые 6 часов (00:00, 06:00, 12:00, 18:00). Хранятся последние 10 версий.
            </p>
            <p className="text-sm text-blue-700 mt-1">
              <strong>Pre-deployment бэкапы:</strong> Автоматически создаются перед каждым развертыванием. Хранятся последние 20 версий.
            </p>
          </div>
        </div>
      </div>

      {/* Backups Table */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Загрузка бэкапов...</p>
        </div>
      ) : backups.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Download className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Нет доступных бэкапов</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Файл
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Тип
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Размер
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Дата создания
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {backups.map((backup, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {backup.filename}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {getBackupTypeBadge(backup.type)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatBytes(backup.size)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(backup.created)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-8 bg-yellow-50 border-l-4 border-yellow-400 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-yellow-800">Восстановление из бэкапа</h3>
            <div className="mt-2 text-sm text-yellow-700">
              <p>Для восстановления данных из бэкапа используйте команду на сервере:</p>
              <code className="block mt-2 bg-yellow-100 p-2 rounded text-xs">
                /app/scripts/restore_backup.sh /app/backups/[filename].tar.gz
              </code>
              <p className="mt-2">Подробная документация: <code>/app/BACKUP_GUIDE.md</code></p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const Reports = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/admin/reports`);
      setReports(response.data.reports);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (reportId, filename) => {
    try {
      const response = await axios.get(`${API}/admin/report/${reportId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(t('admin.reportDownloaded'));
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleExportAllData = async () => {
    try {
      toast.info('Экспорт данных начался... Пожалуйста, подождите.');
      
      const response = await axios.get(`${API}/admin/export-data`, {
        responseType: 'blob'
      });
      
      // Get filename from response headers or generate one
      const filename = `full_export_${new Date().getTime()}.pdf`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('✅ Полный экспорт данных завершён!');
    } catch (error) {
      toast.error('Ошибка экспорта данных: ' + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="p-8" data-testid="admin-reports">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('admin.distributionReports')}</h1>
        <Button onClick={handleExportAllData} className="flex items-center gap-2 bg-green-600 hover:bg-green-700">
          <Download className="h-5 w-5" />
          Экспорт всех данных в PDF
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : reports.length === 0 ? (
        <div className="data-card text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">{t('admin.noReports')}</p>
        </div>
      ) : (
        <div className="data-card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="text-left p-3">{t('date')}</th>
                <th className="text-left p-3">{t('amount')}</th>
                <th className="text-left p-3">Users</th>
                <th className="text-left p-3">{t('admin.individualPayment')}</th>
                <th className="text-left p-3">{t('actions')}</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id} className="border-t border-gray-200" data-testid="report-row">
                  <td className="p-3">
                    {new Date(report.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-3 font-semibold">
                    ${report.distribution?.total_amount.toFixed(2)}
                  </td>
                  <td className="p-3">{report.distribution?.active_users_count}</td>
                  <td className="p-3">${report.distribution?.individual_amount}</td>
                  <td className="p-3">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(report.id, report.filename)}
                      data-testid="download-report-button"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download PDF
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SettingsPage = () => {
  const { language } = useContext(LanguageContext);
  const t = (key) => getTranslation(language, key);

  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTerms();
  }, []);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/admin/terms`);
      setTerms(response.data.content || '');
    } catch (error) {
      toast.error('Failed to load terms');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/terms`, { content: terms });
      toast.success(t('admin.termsUpdated'));
    } catch (error) {
      toast.error('Failed to update terms');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8" data-testid="admin-settings">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('admin.platformSettings')}</h1>

      <div className="data-card max-w-4xl">
        <h3 className="font-semibold text-lg mb-4">Terms and Conditions</h3>
        <p className="text-sm text-gray-600 mb-4">
          Edit the terms and conditions that users must accept during registration.
        </p>
        
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          </div>
        ) : (
          <>
            <textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm"
              data-testid="terms-textarea"
            />
            <Button
              onClick={handleSave}
              disabled={saving}
              className="mt-4"
              data-testid="save-terms-button"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
